// AnthropometricMeasurementForm.tsx
import React, { useEffect, useState } from "react";
import "../../../center admin css/centerAdmin.css";
import api from "../../../api/axiosConfig";

interface FormComponentProps {
  emrId: string;
  onComplete?: () => void;
}

const INITIAL_FORM: AnthropoForm = {
  heightCm: "",
  weightKg: "",
  headCircumferenceCm: "",
  waistCircumferenceCm: "",
  hipCircumferenceCm: "",
  neckCircumferenceCm: "",
  muacCm: "",
  tricepsSkinfoldMm: "",
  subscapularSkinfoldMm: "",
};

interface AnthropoForm {
  heightCm: number | "";
  weightKg: number | "";
  headCircumferenceCm: number | "";
  waistCircumferenceCm: number | "";
  hipCircumferenceCm: number | "";
  neckCircumferenceCm: number | "";
  muacCm: number | "";
  tricepsSkinfoldMm: number | "";
  subscapularSkinfoldMm: number | "";
}

export const AnthropometricMeasurementForm: React.FC<FormComponentProps> = ({
  emrId,
  onComplete,
}) => {
  const [form, setForm] = useState<AnthropoForm>(INITIAL_FORM);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadExistingAnthropometry = async () => {
      if (!emrId) {
        setForm(INITIAL_FORM);
        return;
      }

      try {
        const res = await api.dataCollectionApi.get(
          `/api/forms/anthropometry/${encodeURIComponent(emrId)}`,
        );

        const existingData = res.data?.data;

        if (res.data?.success && existingData) {
          setForm({
            heightCm: existingData.heightCm ?? "",
            weightKg: existingData.weightKg ?? "",
            headCircumferenceCm: existingData.headCircumferenceCm ?? "",
            waistCircumferenceCm: existingData.waistCircumferenceCm ?? "",
            hipCircumferenceCm: existingData.hipCircumferenceCm ?? "",
            neckCircumferenceCm: existingData.neckCircumferenceCm ?? "",
            muacCm: existingData.muacCm ?? "",
            tricepsSkinfoldMm: existingData.tricepsSkinfoldMm ?? "",
            subscapularSkinfoldMm: existingData.subscapularSkinfoldMm ?? "",
          });

          setErrors({});
        } else {
          setForm(INITIAL_FORM);
          setErrors({});
        }
      } catch (err) {
        console.error("Failed to load existing anthropometry:", err);

        // No existing record → keep blank form
        setForm(INITIAL_FORM);
        setErrors({});
      }
    };

    loadExistingAnthropometry();
  }, [emrId]);

  const handleChange =
    (key: keyof AnthropoForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === "" ? "" : Number(e.target.value);
      setForm((prev) => ({ ...prev, [key]: value }));

      if (errors[key]) {
        setErrors((prev) => {
          const n = { ...prev };
          delete n[key];
          return n;
        });
      }
    };

  // const validate = () => {
  //   const newErrors: Record<string, boolean> = {};
  //   Object.entries(form).forEach(([key, value]) => {
  //     if (value === "" || value === undefined || value === null)
  //       newErrors[key] = true;
  //   });
  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };

  const validate = () => {
    const newErrors: Record<string, boolean> = {};

    const requiredFields: (keyof AnthropoForm)[] = [
      "heightCm",
      "weightKg",
      "headCircumferenceCm",
      "waistCircumferenceCm",
      "hipCircumferenceCm",
      "neckCircumferenceCm",
      "muacCm",
    ];

    requiredFields.forEach((key) => {
      const value = form[key];

      if (value === "" || value === undefined || value === null) {
        newErrors[key] = true;
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // ✅ only after successful validation
    onComplete && onComplete();

    const payload = { emrId, ...form };

    try {
      setLoading(true);
      const res = await api.dataCollectionApi.post(
        "/api/forms/anthropometry",
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      if (res.status < 200 || res.status >= 300)
        throw new Error("Failed to submit");
      const data = res.data;

      if (data && data.success) {
        alert("Anthropometric Measurement submitted successfully!");

        // setForm(INITIAL_FORM);

        setErrors({});
      } else {
        alert("Submission failed");
      }
    } catch (err) {
      console.error(err);
      alert("Error submitting form");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (key: string) => (errors[key] ? "input-error" : "");

  return (
    <form onSubmit={handleSubmit} className="form-section">
      <div className="audio-header">Anthropometric Measurement</div>

      <div className="form-body">
        <div className="nurse-grid">
          {/* Height */}
          <div className="n-field">
            <label>Height</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.heightCm ?? ""}
                onChange={handleChange("heightCm")}
                className={inputClass("heightCm")}
              />
              <span className="unit">cm</span>
            </div>
          </div>

          {/* Weight */}
          <div className="n-field">
            <label>Weight</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.weightKg ?? ""}
                onChange={handleChange("weightKg")}
                className={inputClass("weightKg")}
              />
              <span className="unit">kg</span>
            </div>
          </div>

          {/* Head Circumference */}
          <div className="n-field">
            <label>Head Circumference</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.headCircumferenceCm ?? ""}
                onChange={handleChange("headCircumferenceCm")}
                className={inputClass("headCircumferenceCm")}
              />
              <span className="unit">cm</span>
            </div>
          </div>

          {/* Waist Circumference */}
          <div className="n-field">
            <label>Waist Circumference</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.waistCircumferenceCm ?? ""}
                onChange={handleChange("waistCircumferenceCm")}
                className={inputClass("waistCircumferenceCm")}
              />
              <span className="unit">cm</span>
            </div>
          </div>

          {/* Hip Circumference */}
          <div className="n-field">
            <label>Hip Circumference</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.hipCircumferenceCm ?? ""}
                onChange={handleChange("hipCircumferenceCm")}
                className={inputClass("hipCircumferenceCm")}
              />
              <span className="unit">cm</span>
            </div>
          </div>

          {/* Neck Circumference */}
          <div className="n-field">
            <label>Neck Circumference</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.neckCircumferenceCm ?? ""}
                onChange={handleChange("neckCircumferenceCm")}
                className={inputClass("neckCircumferenceCm")}
              />
              <span className="unit">cm</span>
            </div>
          </div>

          {/* Mid-Upper Arm Circumference */}
          <div className="n-field">
            <label>Mid-Upper Arm Circumference</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.muacCm ?? ""}
                onChange={handleChange("muacCm")}
                className={inputClass("muacCm")}
              />
              <span className="unit">cm</span>
            </div>
          </div>

          {/* Triceps Skinfold */}
          <div className="n-field">
            <label>Triceps Skinfold Thickness</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.tricepsSkinfoldMm ?? ""}
                onChange={handleChange("tricepsSkinfoldMm")}
                className={inputClass("tricepsSkinfoldMm")}
              />
              <span className="unit">mm</span>
            </div>
          </div>

          {/* Subscapular Skinfold */}
          <div className="n-field n-span2">
            <label>Subscapular Skinfold</label>
            <div className="n-inline">
              <input
                type="number"
                step="0.1"
                placeholder="Enter value"
                value={form.subscapularSkinfoldMm ?? ""}
                onChange={handleChange("subscapularSkinfoldMm")}
                className={inputClass("subscapularSkinfoldMm")}
              />
              <span className="unit">mm</span>
            </div>
          </div>
        </div>
      </div>

      <div className="audio-actions">
        <button type="button" className="audio-btn audio-btn-secondary">
          Close
        </button>
        <button
          type="submit"
          className="audio-btn audio-btn-primary"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Assessment"}
        </button>
      </div>
    </form>
  );
};
